<template>
  <!-- The Coupons tab of the customer page: the coupon on their Stripe customer,
       the gift cards and referral codes they redeemed, their own
       referral codes, and their invoices that carried a discount. -->
  <v-card flat>
    <coupon-apply-dialog
      v-model="showApply"
      :uid="uid"
      :customer-name="customerName"
      @applied="couponApplied"
    />
    <v-dialog v-model="showRemove" max-width="480px">
      <v-card>
        <v-card-title>Remove the discount?</v-card-title>
        <v-card-text>
          The coupon <code>{{ discountCoupon && discountCoupon.id }}</code> comes off this customer's Stripe
          record, so their next invoices are not discounted by it. The coupon itself is not changed.
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn text :disabled="removing" @click="showRemove = false">Cancel</v-btn>
          <v-btn color="error" :loading="removing" @click="removeDiscount">Remove</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-card-text>
      <v-alert v-if="error" type="error" dense outlined dismissible @input="error = null">{{ error }}</v-alert>
      <v-progress-linear v-if="loading" indeterminate/>

      <template v-if="info">
        <v-alert v-if="!info.stripe_id" type="warning" dense outlined>
          This customer has no Stripe customer, so no coupon can be applied.
        </v-alert>
        <v-alert v-else-if="!info.customer" type="warning" dense outlined>
          Stripe customer <code>{{ info.stripe_id }}</code> was not found on the current Stripe account.
        </v-alert>

        <v-row v-if="info.customer" dense>
          <v-col cols="12">
            <v-card outlined>
              <v-card-title class="subtitle-1">
                Current discount
                <v-spacer/>
                <v-btn small outlined color="primary" class="mr-2" :disabled="!canEditCouponsAndCredit" @click="showApply = true">
                  <v-icon left small>mdi-ticket-percent-outline</v-icon>
                  Apply coupon
                </v-btn>
                <v-btn v-if="discountCoupon" small outlined color="error" :disabled="!canEditCouponsAndCredit" @click="showRemove = true">
                  Remove
                </v-btn>
              </v-card-title>
              <v-card-text>
                <template v-if="discountCoupon">
                  <div>
                    <code>{{ discountCoupon.id }}</code>
                    <span v-if="discountCoupon.name"> - {{ discountCoupon.name }}</span>
                  </div>
                  <div>{{ valueText(discountCoupon) }}, {{ durationText(discountCoupon) }}</div>
                  <div class="caption text--secondary">
                    Since {{ dateFormatted(info.customer.discount.start * 1000, {formatString: 'll'}) }}<span
                    v-if="info.customer.discount.end">, until {{ dateFormatted(info.customer.discount.end * 1000, {formatString: 'll'}) }}</span>
                  </div>
                  <div class="caption text--secondary">
                    Applied to the customer's next invoice<span v-if="discountCoupon.duration !== 'once'">s</span>.
                  </div>
                </template>
                <div v-else class="text--secondary">
                  No coupon on the customer. A gift card applied here is added to their credit instead.
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <div class="subtitle-1 mt-4">Gift cards and referral codes redeemed by this customer</div>
        <v-simple-table v-if="info.redeemed.length" dense>
          <thead>
          <tr>
            <th>Date</th>
            <th>Code</th>
            <th>Name</th>
            <th>Kind</th>
            <th class="text-right">Value</th>
            <th>Account</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="c in info.redeemed" :key="`${c.account}:${c.id}`">
            <td>{{ c.redeemed_at ? dateFormatted(c.redeemed_at * 1000, {formatString: 'll'}) : '' }}</td>
            <td><a :href="`https://dashboard.stripe.com/${c.account_id}/coupons/${c.id}`" target="_blank"><code>{{ c.id }}</code></a></td>
            <td>{{ c.name }}</td>
            <td>{{ c.kind }}</td>
            <td class="text-right">{{ valueText(c) }}</td>
            <td>{{ c.account }}</td>
          </tr>
          </tbody>
        </v-simple-table>
        <div v-else class="caption text--secondary">None recorded. Copy the coupons from Stripe on the Stripe Coupons page if this looks wrong.</div>

        <div class="subtitle-1 mt-4">This customer's referral codes</div>
        <v-simple-table v-if="info.referral_coupons.length" dense>
          <thead>
          <tr>
            <th>Code</th>
            <th>Type</th>
            <th>Used by</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(c, i) in info.referral_coupons" :key="`${c.coupon}-${i}`">
            <td><code>{{ c.coupon }}</code></td>
            <td>{{ c.manager ? 'client invite' : 'referral' }}</td>
            <td>
              <router-link v-if="c.usedBy" :to="{name: 'Customer', params: {id: c.usedBy}}">{{ c.usedBy }}</router-link>
              <span v-else class="text--secondary">not used</span>
            </td>
          </tr>
          </tbody>
        </v-simple-table>
        <div v-else class="caption text--secondary">None.</div>

        <div class="subtitle-1 mt-4">Invoices with a discount (last {{ info.discounted_invoices.length }} of their recent invoices)</div>
        <v-simple-table v-if="info.discounted_invoices.length" dense>
          <thead>
          <tr>
            <th>Date</th>
            <th>Invoice</th>
            <th>Week</th>
            <th>Coupon</th>
            <th class="text-right">Discount</th>
            <th class="text-right">Total</th>
            <th>Status</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="i in info.discounted_invoices" :key="i.id">
            <td>{{ dateFormatted(i.created * 1000, {formatString: 'll'}) }}</td>
            <td>
              <a :href="`https://dashboard.stripe.com/${stripeDashboardAccount(i.id)}/invoices/${i.id}`" target="_blank"><code>{{ i.id }}</code></a>
              <v-chip v-if="i.gift_card_coupon" x-small outlined class="ml-1">gift card credit</v-chip>
            </td>
            <td>
              <router-link v-if="i.week_date" :to="{name: 'CustomerInvoiceComparison', query: {uid, weekDate: i.week_date}}">
                {{ i.week_date }}
              </router-link>
            </td>
            <td>
              <template v-if="i.coupon">
                <code>{{ i.coupon.id }}</code><span v-if="i.coupon.name"> {{ i.coupon.name }}</span>
              </template>
            </td>
            <td class="text-right green--text">{{ formatCurrency(-i.discount) }}</td>
            <td class="text-right">{{ formatCurrency(i.total) }}</td>
            <td>{{ i.status }}</td>
          </tr>
          </tbody>
        </v-simple-table>
        <div v-else class="caption text--secondary">None of their recent invoices carried a discount.</div>
      </template>
    </v-card-text>
  </v-card>
</template>

<script>
import api from '@/api';
import CouponApplyDialog from '@/components/CouponApplyDialog';
import {dateFormatted, formatCurrency} from '@/store/utils';

const OLD_STRIPE_ACCOUNT = 'acct_1B9PlYAhKDCZ2p2o';
const NEW_STRIPE_ACCOUNT = 'acct_1GjcI1D0KVemEdeG';

export default {
  name: 'CustomerCoupons',
  components: {CouponApplyDialog},
  props: {
    uid: {type: String, required: true},
    customerName: {type: String, default: ''},
    // the coupon and credit buttons change what the customer pays
    canEditCouponsAndCredit: {type: Boolean, default: false},
  },
  data() {
    return {
      info: null,
      loading: false,
      error: null,
      showApply: false,
      showRemove: false,
      removing: false,
    };
  },
  computed: {
    discountCoupon() {
      return (this.info && this.info.customer && this.info.customer.discount && this.info.customer.discount.coupon) || null;
    },
  },
  watch: {
    uid() {
      this.load();
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    dateFormatted,
    formatCurrency,

    stripeDashboardAccount(objectId) {
      return typeof objectId === 'string' && objectId.includes(OLD_STRIPE_ACCOUNT.slice(-10)) ? OLD_STRIPE_ACCOUNT : NEW_STRIPE_ACCOUNT;
    },

    async load() {
      this.loading = true;
      this.error = null;
      try {
        const {data} = await api.get(`v2/stripe/coupons/customer/${this.uid}`);
        this.info = data;
      } catch (e) {
        this.error = this.messageOf(e, 'Failed to load the customer\'s coupons');
      } finally {
        this.loading = false;
      }
    },

    // a coupon is a CS note too, so the customer page reloads its notes
    couponApplied() {
      this.load();
      this.$emit('coupon-applied');
    },

    async removeDiscount() {
      this.removing = true;
      try {
        const {data} = await api.delete(`v2/stripe/coupons/customer/${this.uid}/discount`);
        this.info = data;
        this.showRemove = false;
        this.$store.commit('showSnackbar', {message: 'Discount removed from the customer', color: 'success'});
      } catch (e) {
        this.error = this.messageOf(e, 'Failed to remove the discount');
      } finally {
        this.removing = false;
      }
    },

    valueText(c) {
      if (c.percent_off != null) return `${c.percent_off}% off`;
      if (c.amount_off != null) return `${formatCurrency(c.amount_off)} off`;
      return '';
    },

    durationText(c) {
      if (c.duration === 'repeating') return `for ${c.duration_in_months} months`;
      if (c.duration === 'forever') return 'every invoice';
      return 'once';
    },

    messageOf(e, fallback) {
      return (e.response && e.response.data && (e.response.data.message || e.response.data.error)) || e.message || fallback;
    },
  },
};
</script>
